import { connect } from "react-redux";
import cashGif from "../resource/gifs/cash.gif";
import { NavLink } from "react-router-dom";
import styles from "./home.module.css";
import { useSelector, useDispatch } from "react-redux";
import { State } from "../state";
import { useEffect } from "react";
import languageService from "../services/languageService";

function HomePage(): JSX.Element {
  const dispatch = useDispatch();
  const { data: languageData } = useSelector((state: State) => state.languageReducer);
  const { isLoggedIn } = useSelector((state: State) => state.authReducer);
  const homeText = languageData?.home ?? {};

  const handleLanguageChange = async (language: string) => {
    // Update localStorage
    localStorage.setItem("language", language);
    
    // If user is logged in, update language preference in backend
    if (isLoggedIn) {
      try {
        await languageService.updateUserLanguage(language);
      } catch (error) {
        console.error("Failed to update language preference:", error);
      }
    }
    
    // Update Redux state (assuming you have an action for this)
    dispatch({ type: "SET_LANGUAGE", payload: language });
  };

  // Fetch user's language preference when logged in
  useEffect(() => {
    if (isLoggedIn) {
      languageService.getUserLanguage()
        .then(response => {
          const userLanguage = response.data.language;
          if (userLanguage && userLanguage !== localStorage.getItem("language")) {
            handleLanguageChange(userLanguage);
          }
        })
        .catch(error => {
          console.error("Failed to fetch user language preference:", error);
        });
    }
  }, [isLoggedIn]);

  return (
    <div className={styles.container}>
      <div className={styles.hero}>
        <div className={styles["hero-content"]}>
          <h1 className={styles["hero-title"]}>
            <span className={styles["highlight"]}>{homeText.hero?.title?.split(' ')[0]}</span>{' '}
            {homeText.hero?.title?.split(' ').slice(1).join(' ')}
          </h1>
          <p className={styles["hero-subtitle"]}>
            {homeText.hero?.subtitle}
          </p>
          <div className={styles["hero-cta"]}>
            <NavLink to="/contact" className={styles["cta-button"]}>
              {homeText.hero?.getStarted}
            </NavLink>
            <NavLink to="/about" className={styles["secondary-button"]}>
              {homeText.hero?.learnMore}
            </NavLink>
          </div>
        </div>
        <div className={styles["hero-image"]}>
          <div className={styles["image-container"]}>
            <img 
              src="/turpak.png" 
              alt="TURPAK Integration" 
              className={styles["floating-image"]} 
            />
            <p className={styles["image-caption"]}>
              {homeText.hero?.turpakCaption || "Full integrity with Turpak"}
            </p>
          </div>
        </div>
      </div>

      <div className={styles["features-section"]}>
        <div className={styles["section-header"]}>
          <h2>{homeText.features?.title}</h2>
          <p>{homeText.features?.subtitle}</p>
        </div>

        <div className={styles["features-grid"]}>
          <div className={styles["feature-card"]}>
            <div className={styles["feature-icon"]}>🔄</div>
            <h3>{homeText.features?.turpakIntegration.title}</h3>
            <p>{homeText.features?.turpakIntegration.description}</p>
          </div>
          
          <div className={styles["feature-card"]}>
            <div className={styles["feature-icon"]}>💰</div>
            <h3>{homeText.features?.cashFlow.title}</h3>
            <p>{homeText.features?.cashFlow.description}</p>
          </div>
          
          <div className={styles["feature-card"]}>
            <div className={styles["feature-icon"]}>💳</div>
            <h3>{homeText.features?.creditCard.title}</h3>
            <p>{homeText.features?.creditCard.description}</p>
          </div>
          
          <div className={styles["feature-card"]}>
            <div className={styles["feature-icon"]}>👥</div>
            <h3>{homeText.features?.employeeManagement.title}</h3>
            <p>{homeText.features?.employeeManagement.description}</p>
          </div>
        </div>
      </div>

      <div className={styles["workflow-section"]}>
        <div className={styles["section-header"]}>
          <h2>{homeText.workflow?.title}</h2>
          <p>{homeText.workflow?.subtitle}</p>
        </div>

        <div className={styles["steps-container"]}>
          {homeText.workflow?.steps.map((step: string, index: number) => (
            <div className={styles.step} key={index}>
              <div className={styles["step-number"]}>{index + 1}</div>
              <p>{step}</p>
            </div>
          ))}
        </div>
      </div>

      <div className={styles["cta-section"]}>
        <h2>{homeText.cta?.title}</h2>
        <p>{homeText.cta?.subtitle}</p>
        <NavLink to="/contact" className={styles["cta-button"]}>
          {homeText.cta?.button}
        </NavLink>
      </div>
    </div>
  );
}

function mapStateToProps(state: any) {
  const { isLoggedIn } = state.authReducer ?? {};
  const { message } = state.messageReducer ?? {};
  return {
    isLoggedIn,
    message,
  };
}

export default connect(mapStateToProps)(HomePage);
